import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Eye, Pencil } from "lucide-react";
import { listarMotivosAjuste } from "../services/ajustes";
import { useFecharComEsc } from "../hooks/useFecharComEsc";
import { MentionTextarea } from "./MentionTextarea";
import { listarUsuariosAtivos } from "../services/usuarios";
import { uploadAnexo, anexoUrlAbsoluto } from "../services/tarefas";

export interface DadosAjuste {
  categoria: "interno" | "cliente";
  motivoId: string | null;
  descricao: string | null;
  arquivos: File[];
}

/**
 * Modal pra registrar um ajuste ao mover uma tarefa para a coluna "Ajustes".
 * Captura categoria (interno/cliente) + motivo (catálogo editável) + descrição.
 */
export function RegistrarAjusteModal({ onConfirmar, onCancelar, salvando, tarefaId }: {
  onConfirmar: (dados: DadosAjuste) => void;
  onCancelar: () => void;
  salvando?: boolean;
  /** Necessário pra colar/arrastar imagem na observação (a imagem vira anexo da tarefa). */
  tarefaId?: string;
}) {
  const [categoria, setCategoria] = useState<"interno" | "cliente">("interno");
  const [motivoId, setMotivoId] = useState<string>("");
  const [descricao, setDescricao] = useState("");
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [recolhido, setRecolhido] = useState(false);

  const { data: motivos = [] } = useQuery({
    queryKey: ["motivos-ajuste", "ativos"],
    queryFn: () => listarMotivosAjuste(),
  });
  // Pro @menção do editor (mesma lista usada nos comentários).
  const { data: usuarios = [] } = useQuery({ queryKey: ["usuarios-ativos"], queryFn: listarUsuariosAtivos });

  // ESC fecha (pedido da Thainá), mas confirma se já tem algo preenchido.
  useFecharComEsc(onCancelar, () => descricao.trim().length > 0 || arquivos.length > 0 || !!motivoId);

  // RECOLHIDO: a pessoa precisa ler algo do card pra escrever o ajuste (pedido da
  // Thainá, ago/2026). Recolher some com a janela sem perder NADA do que já foi
  // escrito — o componente continua montado, só não é exibido.
  if (recolhido) {
    return (
      <div className="fixed bottom-4 right-4 z-[70]">
        <button
          type="button"
          onClick={() => setRecolhido(false)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-full shadow-2xl text-sm font-medium"
          title="Voltar a escrever o ajuste"
        >
          <Pencil size={15} />
          Voltar ao ajuste
          <span className="text-[10px] bg-white/25 rounded-full px-2 py-0.5">rascunho guardado</span>
        </button>
      </div>
    );
  }

  return (
    // Clique no fundo NÃO fecha: a Thainá perdeu o ajuste que estava escrevendo
    // duas vezes assim. Só o X, o Cancelar ou o ESC fecham.
    // pointer-events-none no container + auto na caixa: o fundo PARA DE BLOQUEAR,
    // então dá pra rolar o card e selecionar/copiar texto de lá com o ajuste
    // aberto (era impossível antes — a camada escura comia todo clique).
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
      <div className="absolute inset-0 bg-black/30 pointer-events-none" />
      {/* max-h + flex-col: sem isso a caixa CRESCIA junto com o conteúdo e, com
          várias imagens coladas, o começo/fim ficavam fora da tela sem rolagem
          (relato da Thainá, ago/2026). Agora o miolo rola e cabeçalho/botões
          ficam sempre visíveis. */}
      <div className="relative bg-white dark:bg-slate-900 rounded-xl w-full max-w-md shadow-2xl max-h-[88vh] flex flex-col pointer-events-auto">
        <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2 shrink-0">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100">Registrar ajuste</h3>
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setRecolhido(true)}
              className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/40 dark:text-blue-200"
              title="Recolher pra ler o card — o que você escreveu fica guardado"
            >
              <Eye size={14} /> Ver o card
            </button>
            <button
              onClick={onCancelar}
              className="p-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white shadow-sm"
              title="Fechar (ESC)"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto flex-1 min-h-0">
          <p className="text-xs text-slate-500">
            O tempo trabalhado enquanto a tarefa estiver em "Ajustes" será contabilizado para este motivo.
          </p>
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 block mb-1">Categoria</label>
            <div className="inline-flex rounded-lg border border-slate-300 dark:border-slate-600 overflow-hidden">
              {([
                { id: "interno", label: "Ajuste interno" },
                { id: "cliente", label: "Ajuste do cliente" },
              ] as const).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setCategoria(opt.id)}
                  className={
                    "px-4 py-2 text-sm font-medium transition-colors " +
                    (categoria === opt.id ? "bg-blue-600 text-white" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50")
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 block mb-1">
              Motivo <span className="text-red-500">*</span>
            </label>
            <select
              value={motivoId}
              onChange={(e) => setMotivoId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 outline-none text-sm focus:border-blue-500"
            >
              <option value="">Selecione um motivo…</option>
              {motivos.map((m) => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 block mb-1">Observação <span className="text-slate-400">(opcional)</span></label>
            {/* Editor rico (igual comentário/ajuste do card): dá pra colar (Ctrl+V)
                ou ARRASTAR a imagem pra dentro do texto, além de negrito/cor/listas.
                A imagem sobe como anexo da tarefa e entra inline na descrição —
                por isso precisa do tarefaId. Sem ele (caso não seja passado),
                cai no editor simples, sem upload. */}
            <MentionTextarea
              value={descricao}
              onChange={setDescricao}
              usuarios={usuarios}
              toolbar
              rows={3}
              placeholder="Detalhe o ajuste… (cole com Ctrl+V ou arraste a imagem pra cá)"
              onUploadImagem={
                tarefaId
                  ? (file) => uploadAnexo(tarefaId, file).then((a) => anexoUrlAbsoluto(a.url))
                  : undefined
              }
            />
          </div>
          <div>
            <label className="text-xs text-slate-600 dark:text-slate-300 block mb-1">
              Anexos <span className="text-slate-400">(fotos, vídeos, PDF, áudio — opcional)</span>
            </label>
            <input
              type="file"
              multiple
              accept="image/*,video/*,audio/*,application/pdf"
              onChange={(e) => {
                const escolhidos = Array.from(e.target.files || []);
                const validos = escolhidos.filter((f) => f.size <= 25 * 1024 * 1024);
                if (validos.length < escolhidos.length) alert("Alguns arquivos passam de 25 MB e foram ignorados.");
                if (validos.length) setArquivos((prev) => [...prev, ...validos]);
                e.target.value = "";
              }}
              className="block w-full text-xs text-slate-600 dark:text-slate-300 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 file:cursor-pointer"
            />
            {arquivos.length > 0 && (
              <ul className="mt-2 space-y-1">
                {arquivos.map((f, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 text-xs bg-slate-50 dark:bg-slate-800 rounded px-2 py-1">
                    <span className="truncate">
                      {f.name} <span className="text-slate-400">({(f.size / (1024 * 1024)).toFixed(1)} MB)</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setArquivos((prev) => prev.filter((_, idx) => idx !== i))}
                      className="text-slate-400 hover:text-red-500 shrink-0"
                      title="Remover"
                    >
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2 shrink-0">
          {/* Sem motivo o ajuste vira uma linha "Sem motivo" no relatório, que
              não dá pra usar pra nada — por isso o botão só libera com ele. */}
          {!motivoId && (
            <span className="text-[11px] text-amber-600 self-center mr-auto">Escolha o motivo para continuar</span>
          )}
          <button onClick={onCancelar} className="px-3 py-1.5 rounded text-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200">Cancelar</button>
          <button
            onClick={() => onConfirmar({ categoria, motivoId, descricao: descricao.trim() || null, arquivos })}
            disabled={salvando || !motivoId}
            title={!motivoId ? "Escolha o motivo do ajuste" : undefined}
            className="px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {salvando ? "Salvando…" : "Registrar e mover"}
          </button>
        </div>
      </div>
    </div>
  );
}
